<template>
    <div :class="['analytics-hero-stat', variantClass]">
        <div class="hero-label">{{ label }}</div>
        <div class="hero-value">
            {{ formattedValue }}<span v-if="unit" class="hero-unit">{{ unit }}</span>
        </div>
        <div v-if="change !== null" :class="['hero-change', changeClass]">
            <span class="arrow">{{ change >= 0 ? '↑' : '↓' }}</span>
            {{ Math.abs(change).toFixed(1) }}%
        </div>

        <!-- Optional sparkline slot -->
        <div v-if="sparklineData" class="hero-sparkline">
            <slot name="sparkline">
                <line-chart
                    :data="sparklineData"
                    :colors="[sparklineColor]"
                    height="40px"
                    :library="{
                        scales: {
                            y: { display: false },
                            x: { display: false }
                        },
                        plugins: { legend: { display: false } },
                        elements: { point: { radius: 0 }, line: { borderWidth: 2 } }
                    }"
                />
            </slot>
        </div>
    </div>
</template>

<script>
export default {
    name: 'AnalyticsHeroStat',
    props: {
        label: {
            type: String,
            required: true
        },
        value: {
            type: [Number, String],
            required: true
        },
        unit: {
            type: String,
            default: null
        },
        format: {
            type: String,
            default: 'compact' // number, compact, currency, percent
        },
        change: {
            type: Number,
            default: null
        },
        variant: {
            type: String,
            default: 'default' // default, blue, green, amber
        },
        sparklineData: {
            type: [Array, Object],
            default: null
        },
        apiUrl: {
            type: String,
            default: null
        },
        refreshInterval: {
            type: Number,
            default: 10000
        }
    },
    data() {
        return {
            localValue: this.value,
            pollTimer: null
        };
    },
    computed: {
        variantClass() {
            return this.variant !== 'default' ? `hero-stat--${this.variant}` : '';
        },
        changeClass() {
            if (this.change === null) return '';
            return this.change >= 0 ? 'positive' : 'negative';
        },
        sparklineColor() {
            const colors = {
                default: '#8b5cf6',
                blue: '#3b82f6',
                green: '#22c55e',
                amber: '#f59e0b'
            };
            return colors[this.variant] || colors.default;
        },
        formattedValue() {
            const val = this.localValue;
            if (val === null || val === undefined) return '—';

            switch (this.format) {
                case 'number':
                    return Number(val).toLocaleString();
                case 'compact':
                    return Number(val).toLocaleString(undefined, {
                        notation: 'compact',
                        maximumFractionDigits: 1
                    });
                case 'currency':
                    return '$' + Number(val).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    });
                case 'percent':
                    return Number(val).toFixed(1) + '%';
                default:
                    return val;
            }
        }
    },
    mounted() {
        if (this.apiUrl) {
            this.fetchData();
            this.pollTimer = setInterval(this.fetchData, this.refreshInterval);
        }
    },
    beforeUnmount() {
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
        }
    },
    methods: {
        async fetchData() {
            if (!this.apiUrl) return;
            try {
                const response = await axios.get(this.apiUrl);
                if (response.data && response.data.value !== undefined) {
                    this.localValue = response.data.value;
                }
            } catch (error) {
                console.error('AnalyticsHeroStat fetch error:', error);
            }
        }
    },
    watch: {
        value(newVal) {
            this.localValue = newVal;
        }
    }
};
</script>
